import {
  EOrderStatus,
  EWithdrawalStatus,
  type IAdminWithdrawalDetail,
  WITHDRAWAL_TEXT_MAX_LENGTH,
} from '@mahawi/eshop-common/src/types';
import { Alert, Button, Form, Input, Popconfirm } from 'antd';
import React from 'react';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { withdrawalCancelOrder } from '../../reducers/withdrawal/reducer';
import { SPACING } from '../../theme/tokens';

interface ICancelOrderFormValues {
  note?: string;
}

interface ICancelOrderPanelProps {
  detail: IAdminWithdrawalDetail;
  inProcess: boolean;
}

export const canCancelWithdrawalOrder = (
  detail: IAdminWithdrawalDetail,
): boolean =>
  detail.order?.orderStatusCode === EOrderStatus.CREATED &&
  detail.status !== EWithdrawalStatus.ORDER_CANCELLED;

function CancelOrderPanel({
  detail,
  inProcess,
}: ICancelOrderPanelProps): React.ReactElement | null {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<ICancelOrderFormValues>();

  if (!canCancelWithdrawalOrder(detail)) {
    return null;
  }

  return (
    <Form form={form} layout="vertical">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: SPACING.md }}
        title="The linked order has not been paid or shipped yet."
        description="Cancelling it closes this withdrawal, switches the order to cancelled and emails the customer. A later payment expiry from the gateway is then ignored."
      />
      <Form.Item
        name="note"
        label="Note (optional)"
        rules={[{ max: WITHDRAWAL_TEXT_MAX_LENGTH }]}
      >
        <Input.TextArea rows={2} disabled={inProcess} />
      </Form.Item>
      <Popconfirm
        title="Cancel the order?"
        description="The customer will receive the cancellation email."
        okText="Cancel order"
        cancelText="Back"
        okButtonProps={{ danger: true }}
        onConfirm={async (): Promise<void> => {
          const values: ICancelOrderFormValues = await form.validateFields();

          dispatch(
            withdrawalCancelOrder({
              uuid: detail.uuid,
              note: values.note?.trim() ? values.note.trim() : null,
            }),
          );
          form.resetFields();
        }}
      >
        <Button danger disabled={inProcess}>
          Cancel order
        </Button>
      </Popconfirm>
    </Form>
  );
}

export default CancelOrderPanel;
